/**
 * Eingebaute Inhalte: Aufgaben-Typen, Pflanzen-Steckbriefe und die Startdaten.
 *
 * Alles hier ist nur ein Startwert. In der App lassen sich eigene Aufgaben-Typen
 * und Steckbriefe anlegen und alle Abstände pro Pflanze ändern – ohne Code.
 * Neue eingebaute Einträge einfach unten in die Listen schreiben.
 */
import { Settings, Species, TaskType, type AppState, type CareTask, type Plant, type Room } from './model';
import { API_VERSION } from './model';

export const BUILTIN_TASK_TYPES: TaskType[] = [
  {
    key: 'water',
    label: 'Gießen',
    action: 'Erde prüfen',
    doneLabel: 'Gegossen',
    icon: '💧',
    defaultIntervalDays: 7,
    fingerTest: true,
    learn: true,
    winter: 'stretch',
    firstDue: 'now',
    points: 10,
    dryAirBoost: false,
    description:
      'Finger in die Erde stecken. Ist sie so trocken, wie die Pflanze es mag? Dann gründlich gießen, bis unten Wasser herausläuft. Sonst: „Noch feucht“ – die App lernt daraus.',
    builtin: true,
  },
  {
    key: 'humidity',
    label: 'Luftfeuchte',
    action: 'Befeuchten',
    doneLabel: 'Befeuchtet',
    icon: '💨',
    defaultIntervalDays: 2,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'now',
    points: 3,
    dryAirBoost: true,
    description:
      'Am besten morgens, damit die Blätter bis abends trocknen: Kiesschale mit Wasser auffüllen, Luftbefeuchter an oder leicht sprühen. Pflanzen eng zusammenstellen hilft zusätzlich.',
    builtin: true,
  },
  {
    key: 'fertilize',
    label: 'Düngen',
    action: 'Düngen',
    doneLabel: 'Gedüngt',
    icon: '🧪',
    defaultIntervalDays: 42,
    fingerTest: false,
    learn: false,
    winter: 'pause',
    firstDue: 'after_interval',
    points: 5,
    dryAirBoost: false,
    description:
      'Flüssigdünger nach Packungsangabe ins Gießwasser, nur in feuchte Erde. Im Winter (Tage kürzer als 10½ Stunden) macht die App automatisch Pause.',
    builtin: true,
  },
  {
    key: 'flush',
    label: 'Durchspülen',
    action: 'Erde durchspülen',
    doneLabel: 'Durchgespült',
    icon: '🚿',
    defaultIntervalDays: 150,
    fingerTest: false,
    learn: false,
    winter: 'pause',
    firstDue: 'after_interval',
    points: 5,
    dryAirBoost: false,
    description:
      'Topf in Dusche oder Spüle langsam mit viel Wasser durchlaufen lassen, damit sich keine Dünger-Salze ansammeln. Danach gut abtropfen lassen.',
    builtin: true,
  },
  {
    key: 'rotate',
    label: 'Drehen',
    action: 'Topf drehen',
    doneLabel: 'Gedreht',
    icon: '🔄',
    defaultIntervalDays: 14,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'after_interval',
    points: 2,
    dryAirBoost: false,
    description: 'Eine Vierteldrehung, damit die Pflanze gleichmäßig zum Licht wächst und nicht schief wird.',
    builtin: true,
  },
  {
    key: 'dust',
    label: 'Blätter abwischen',
    action: 'Blätter abwischen',
    doneLabel: 'Abgewischt',
    icon: '🧽',
    defaultIntervalDays: 30,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'after_interval',
    points: 3,
    dryAirBoost: false,
    description: 'Staub mit einem feuchten Tuch abwischen – saubere Blätter bekommen mehr Licht.',
    builtin: true,
  },
  {
    key: 'pests',
    label: 'Schädlinge prüfen',
    action: 'Blattunterseiten ansehen',
    doneLabel: 'Geprüft',
    icon: '🔍',
    defaultIntervalDays: 14,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'after_interval',
    points: 3,
    dryAirBoost: false,
    description: 'Blattunterseiten und Blattachseln ansehen: feine Gespinste, weiße Watte, braune Pünktchen oder klebrige Stellen?',
    builtin: true,
  },
  {
    key: 'repot',
    label: 'Umtopfen prüfen',
    action: 'Wurzeln prüfen',
    doneLabel: 'Geprüft',
    icon: '🪴',
    defaultIntervalDays: 365,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'after_interval',
    points: 5,
    dryAirBoost: false,
    description: 'Kommen unten Wurzeln heraus oder ist der Topf ganz durchwurzelt? Dann im Frühling in einen etwas größeren Topf umziehen.',
    builtin: true,
  },
];

const S = (s: Omit<Species, 'builtin'> & { builtin?: boolean }): Species => Species.parse({ ...s, builtin: true });

export const BUILTIN_SPECIES: Species[] = [
  S({
    key: 'calathea-ornata',
    name: 'Calathea ornata',
    aliases: ['Korbmarante', 'Goeppertia ornata', 'Nadelstreifen-Calathea'],
    emoji: '🌿',
    waterDays: 5,
    winterStretch: 0.3,
    idealSoil: 'slightly_moist',
    fingerDepthCm: 2,
    humidityTarget: 60,
    tempMinC: 18,
    tempMaxC: 24,
    directSun: 'avoid',
    draftSensitive: true,
    fluorideSensitive: true,
    toxicity: null,
    tasks: { water: 5, humidity: 2, fertilize: 30, flush: 150, rotate: 14, pests: 14 },
    light: 'Hell, aber ohne direkte Sonne (Halbschatten).',
    tips: [
      'Erde gleichmäßig leicht feucht halten – nicht ganz austrocknen lassen, aber auch nie nass stehen lassen.',
      'Braune Blattränder kommen meist von kalter Zugluft, trockener Luft oder zu viel direkter Sonne.',
      'Die Blätter heben sich abends und senken sich morgens – das ist normal und ein gutes Zeichen.',
      'In der Wachstumszeit etwa monatlich mit verdünntem Flüssigdünger düngen.',
    ],
    sourceUrl: 'https://plants.ces.ncsu.edu/plants/goeppertia-ornata/',
  }),
  S({
    key: 'dieffenbachia',
    name: 'Dieffenbachia',
    aliases: ['Dieffenbachia Compacta', 'Dieffenbachie', 'Dieffenbachia seguine', 'Schweigrohr'],
    emoji: '🪴',
    waterDays: 7,
    winterStretch: 0.4,
    idealSoil: 'dry',
    fingerDepthCm: 2.5,
    humidityTarget: 55,
    tempMinC: 16,
    tempMaxC: 27,
    directSun: 'avoid',
    draftSensitive: true,
    fluorideSensitive: false,
    toxicity: 'Giftig für Kinder und Haustiere (Kalziumoxalat). Der Saft reizt Haut und Mund – beim Schneiden Handschuhe tragen.',
    tasks: { water: 7, fertilize: 42, flush: 150, rotate: 14, dust: 30 },
    light: 'Hell bis halbschattig, keine direkte Mittagssonne.',
    tips: [
      'Gründlich gießen, dann die obersten ~2,5 cm Erde ganz abtrocknen lassen, bevor wieder gegossen wird.',
      'Vor kalter Zugluft und direkter Sonne schützen; mag eher hohe Luftfeuchte.',
      'Große Blätter regelmäßig abwischen – sie fangen viel Staub.',
    ],
    sourceUrl: 'https://plants.ces.ncsu.edu/plants/dieffenbachia-seguine/',
  }),
  S({
    key: 'sansevieria-cylindrica',
    name: 'Sansevieria cylindrica',
    aliases: ['Bogenhanf', 'Dracaena angolensis', 'Speer-Sansevierie', 'Zylindrischer Bogenhanf'],
    emoji: '🎋',
    waterDays: 14,
    winterStretch: 1.0,
    idealSoil: 'dry',
    fingerDepthCm: 5,
    humidityTarget: 40,
    tempMinC: 13,
    tempMaxC: 30,
    directSun: 'tolerant',
    draftSensitive: false,
    fluorideSensitive: false,
    toxicity: 'Leicht giftig (Saponine) – nicht anknabbern lassen.',
    tasks: { water: 14, fertilize: 56, rotate: 21 },
    light: 'Hell; verträgt auch etwas direkte Sonne.',
    tips: [
      'Nur gießen, wenn die Erde ganz trocken ist: im Sommer etwa alle 2 Wochen, im Winter etwa einmal im Monat.',
      'Zu viel Wasser ist die häufigste Todesursache – im Zweifel lieber noch warten.',
      'Hält auch mal längere Trockenheit aus – ideal, wenn du im Urlaub bist.',
    ],
    sourceUrl: 'https://plants.ces.ncsu.edu/plants/dracaena-angolensis/',
  }),
  S({
    key: 'monstera',
    name: 'Monstera',
    aliases: ['Fensterblatt', 'Monstera deliciosa'],
    emoji: '🌱',
    waterDays: 7,
    winterStretch: 0.5,
    idealSoil: 'dry',
    fingerDepthCm: 3,
    humidityTarget: 50,
    tempMinC: 16,
    tempMaxC: 28,
    directSun: 'morning_ok',
    draftSensitive: true,
    fluorideSensitive: false,
    toxicity: 'Giftig für Haustiere (Kalziumoxalat).',
    tasks: { water: 7, fertilize: 30, rotate: 14, dust: 30 },
    light: 'Hell, ohne pralle Mittagssonne.',
    tips: ['Obere Erdschicht antrocknen lassen, dann gründlich gießen.', 'Große Blätter regelmäßig abwischen.'],
    sourceUrl: null,
  }),
  S({
    key: 'efeutute',
    name: 'Efeutute',
    aliases: ['Epipremnum', 'Pothos', 'Epipremnum aureum'],
    emoji: '🍃',
    waterDays: 7,
    winterStretch: 0.5,
    idealSoil: 'dry',
    fingerDepthCm: 2,
    humidityTarget: 45,
    tempMinC: 15,
    tempMaxC: 30,
    directSun: 'morning_ok',
    draftSensitive: false,
    fluorideSensitive: false,
    toxicity: 'Giftig für Haustiere (Kalziumoxalat).',
    tasks: { water: 7, fertilize: 42, rotate: 21 },
    light: 'Hell bis halbschattig.',
    tips: ['Verzeiht fast alles – außer Staunässe.'],
    sourceUrl: null,
  }),
  S({
    key: 'zamioculcas',
    name: 'Zamioculcas',
    aliases: ['Glücksfeder', 'ZZ-Pflanze'],
    emoji: '🌾',
    waterDays: 14,
    winterStretch: 1.0,
    idealSoil: 'dry',
    fingerDepthCm: 5,
    humidityTarget: 40,
    tempMinC: 15,
    tempMaxC: 30,
    directSun: 'morning_ok',
    draftSensitive: false,
    fluorideSensitive: false,
    toxicity: 'Leicht giftig.',
    tasks: { water: 14, fertilize: 56 },
    light: 'Hell bis schattig.',
    tips: ['Speichert Wasser in den Knollen – erst gießen, wenn die Erde ganz trocken ist.'],
    sourceUrl: null,
  }),
  S({
    key: 'einblatt',
    name: 'Einblatt',
    aliases: ['Spathiphyllum', 'Friedenslilie'],
    emoji: '🤍',
    waterDays: 5,
    winterStretch: 0.4,
    idealSoil: 'slightly_moist',
    fingerDepthCm: 2,
    humidityTarget: 55,
    tempMinC: 16,
    tempMaxC: 28,
    directSun: 'avoid',
    draftSensitive: true,
    fluorideSensitive: false,
    toxicity: 'Giftig für Haustiere (Kalziumoxalat).',
    tasks: { water: 5, fertilize: 30, dust: 30 },
    light: 'Halbschatten.',
    tips: ['Lässt die Blätter hängen, wenn sie Durst hat – erholt sich nach dem Gießen schnell.'],
    sourceUrl: null,
  }),
  S({
    key: 'kaktus',
    name: 'Kaktus / Sukkulente',
    aliases: ['Sukkulente', 'Kakteen'],
    emoji: '🌵',
    waterDays: 14,
    winterStretch: 1.5,
    idealSoil: 'dry',
    fingerDepthCm: 3,
    humidityTarget: 35,
    tempMinC: 8,
    tempMaxC: 35,
    directSun: 'tolerant',
    draftSensitive: false,
    fluorideSensitive: false,
    toxicity: null,
    tasks: { water: 14, fertilize: 60 },
    light: 'So hell wie möglich, gern Sonne.',
    tips: ['Im Winter fast gar nicht gießen.'],
    sourceUrl: null,
  }),
  S({
    key: 'allgemein',
    name: 'Andere Zimmerpflanze',
    aliases: [],
    emoji: '🪴',
    waterDays: 7,
    winterStretch: 0.4,
    idealSoil: 'dry',
    fingerDepthCm: 2,
    humidityTarget: 50,
    tempMinC: 15,
    tempMaxC: 28,
    directSun: 'morning_ok',
    draftSensitive: false,
    fluorideSensitive: false,
    toxicity: null,
    tasks: { water: 7, fertilize: 42, rotate: 21 },
    light: '',
    tips: ['Startwerte – die App lernt aus deinen Fingertests, wie oft diese Pflanze wirklich Wasser braucht.'],
    sourceUrl: null,
  }),
];

/** Steckbriefe: eingebaute + eigene (eigene überschreiben eingebaute mit gleichem Schlüssel). */
export function allSpecies(custom: Species[]): Species[] {
  const map = new Map<string, Species>();
  for (const s of BUILTIN_SPECIES) map.set(s.key, s);
  for (const s of custom) map.set(s.key, s);
  return [...map.values()];
}

/** Aufgaben-Typen: eingebaute + eigene (eigene überschreiben eingebaute mit gleichem Schlüssel). */
export function allTaskTypes(custom: TaskType[]): TaskType[] {
  const map = new Map<string, TaskType>();
  for (const t of BUILTIN_TASK_TYPES) map.set(t.key, t);
  for (const t of custom) map.set(t.key, t);
  return [...map.values()];
}

export function findSpecies(key: string | null | undefined, custom: Species[]): Species | undefined {
  if (!key) return undefined;
  return allSpecies(custom).find((s) => s.key === key);
}

/** Aufgaben, die eine neue Pflanze dieser Art bekommt. */
export function tasksForNewPlant(plant: Plant, species: Species | undefined, nowIso: string): CareTask[] {
  const entries = Object.keys(species?.tasks ?? { water: 7 });
  return entries.map((typeKey) => ({
    id: `${plant.id}-${typeKey}`,
    plantId: plant.id,
    typeKey,
    intervalDays: null,
    enabled: true,
    pausedUntil: null,
    createdAt: nowIso,
  }));
}

/** Startdaten mit Alex' Pflanzen (Stand: September 2026). */
export function createSeed(nowIso: string): AppState {
  const room: Room = {
    id: 'zimmer',
    name: 'Zimmer',
    windowAzimuth: 180,
    plantSide: 'right',
    window: 'closed',
    curtain: 'open',
    windowChangedAt: null,
    curtainChangedAt: null,
  };
  const base = { roomId: room.id, potMaterial: 'plastic' as const, potDiameterCm: null, waterType: 'tap' as const, hasSaucer: true, notes: '', coverPhotoId: null, createdAt: nowIso, archivedAt: null };
  const plants: Plant[] = [
    { ...base, id: 'calathea', name: 'Calathea Ornata', slug: 'calathea', speciesKey: 'calathea-ornata', emoji: '🌿' },
    { ...base, id: 'dieffenbachia', name: 'Dieffenbachia Compacta', slug: 'dieffenbachia', speciesKey: 'dieffenbachia', emoji: '🪴' },
    { ...base, id: 'bogenhanf', name: 'Bogenhanf', slug: 'bogenhanf', speciesKey: 'sansevieria-cylindrica', emoji: '🎋' },
  ];
  const tasks = plants.flatMap((p) => tasksForNewPlant(p, findSpecies(p.speciesKey, []), nowIso));
  return {
    apiVersion: API_VERSION,
    serverTime: nowIso,
    settings: Settings.parse({}),
    rooms: [room],
    plants,
    taskTypes: [],
    tasks,
    events: [],
    photos: [],
    species: [],
  };
}
